import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { getErrorMessage } from '../../utils/errorHandler';
import imponiblesAPI, {
  ImponibleData,
  ImponiblePayload,
} from './imponiblesAPI';

interface ImponiblesState {
  status: 'loading' | 'idle' | 'error' | 'success';
  error?: string;
  list: ImponibleData[];
  imponible?: ImponibleData;
  message?: string;
  lastUpdate: number;
}

export const initialState: ImponiblesState = {
  status: 'idle',
  list: [],
  lastUpdate: -1,
};

export const getImponiblesByCuit = createAsyncThunk<
  ImponibleData[],
  void,
  { rejectValue: string }
>('suscripciones/getByCuit', async (_, thunkAPI) => {
  try {
    const response = await imponiblesAPI.getImponiblesByCuit();

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const createImponibleByCuit = createAsyncThunk<
  ImponibleData,
  ImponiblePayload,
  { rejectValue: string }
>('suscripciones', async (payload, thunkAPI) => {
  try {
    const response = await imponiblesAPI.createImponible(payload);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getImponible = createAsyncThunk<
  ImponibleData,
  number,
  { rejectValue: string }
>('suscripciones/imponible', async (id, thunkAPI) => {
  try {
    const response = await imponiblesAPI.getImponible(id);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const unlinkImponible = createAsyncThunk<
  ImponibleData,
  number,
  { rejectValue: string }
>('suscripciones/delete', async (id, thunkAPI) => {
  try {
    const response = await imponiblesAPI.unlinkImponible(id);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }
    return { ...response.data, id };
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});


export const ImponiblesState = createSlice({
  name: 'ImponiblesState',
  initialState,
  reducers: {
    cleanImponibleState(state) {
      state.status = 'idle';
      state.error = '';
      state.message = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getImponiblesByCuit.pending, state => {
        state.status = 'loading';
      })
      .addCase(getImponiblesByCuit.fulfilled, (state, action) => {
        state.status = 'idle';
        state.list = action.payload;
        state.lastUpdate = Date.now();
      })
      .addCase(getImponiblesByCuit.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(createImponibleByCuit.pending, state => {
        state.status = 'loading';
      })
      .addCase(createImponibleByCuit.fulfilled, (state, action) => {
        state.status = 'idle';
        state.message = 'Imponible agregado!';
        state.list = [...state.list, action.payload];
      })
      .addCase(createImponibleByCuit.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(getImponible.pending, state => {
        state.status = 'loading';
      })
      .addCase(getImponible.fulfilled, (state, action) => {
        state.status = 'idle';
        state.imponible = action.payload;
      })
      .addCase(getImponible.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(unlinkImponible.pending, state => {
        state.status = 'loading';
      })
      .addCase(unlinkImponible.fulfilled, (state, action) => {
        state.status = 'success';
        state.lastUpdate = Date.now();
        state.list = state.list.filter(
          item => item.id !== action.payload.id
        );
      })
      .addCase(unlinkImponible.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
  },
});

export const getImponiblesList = (state: RootState) => state.imponibles.list;
export const getImponiblesListUpdateTime = (state: RootState) => state.imponibles.lastUpdate;
export const singleImponible = (state: RootState) => state.imponibles.imponible ?? undefined;

export const errorImponible = (state: RootState) => state.imponibles.error;
export const statusImponible = (state: RootState) => state.imponibles.status;
export const messageImponible = (state: RootState) => state.imponibles.message;
export const { cleanImponibleState } = ImponiblesState.actions;

export default ImponiblesState.reducer;
